/**
 * Sanitização e assertividade: tabela markdown Orya × texto introdutório.
 */

function splitCells(line) {
  const trimmed = String(line || '').trim();
  if (!trimmed.includes('|')) return null;
  const inner = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return inner.split('|').map((c) => c.trim());
}

function padRow(row, colCount) {
  const padded = [...(row || [])];
  while (padded.length < colCount) padded.push('');
  return padded.slice(0, colCount);
}

function headerIndex(headers, pattern) {
  return (headers || []).findIndex((h) => pattern.test(String(h || '')));
}

function serieNumberColumnIndex(headers) {
  return (headers || []).findIndex((h) => {
    const t = String(h || '').trim().toLowerCase();
    if (!t || /quantidade|qtde|qtd\b|percentual|chamado|modelo|equipamento|total/i.test(t)) {
      return false;
    }
    return (
      /^s[ée]rie$|^n[úu]mero(\s+de)?\s+s[ée]rie$|^s[ée]rie\s+do\s+equip/i.test(t)
      || (/\bn[úu]mero\b/.test(t) && /\bs[ée]rie\b/.test(t))
    );
  });
}

/** Linha incompleta (resposta cortada ou células vazias demais). */
export function isCompleteTableRow(headers, row) {
  const colCount = headers?.length || 0;
  if (colCount < 1) return false;
  if (colCount === 1) {
    const cell = String(row?.[0] ?? '').trim();
    return Boolean(cell && cell !== '-');
  }
  const cells = padRow(row, colCount);
  const nonEmpty = cells.filter((c) => {
    const v = String(c ?? '').trim();
    return v && v !== '-';
  }).length;
  if (nonEmpty < Math.max(2, Math.ceil(colCount * 0.65))) return false;

  const serieIdx = serieNumberColumnIndex(headers);
  if (serieIdx >= 0) {
    const serie = String(cells[serieIdx] ?? '').trim();
    if (!serie || serie.length < 10) return false;
  }

  const qtdeIdx = headerIndex(headers, /qtde|quantidade|peças trocadas|pecas trocadas/i);
  if (qtdeIdx >= 0) {
    const qtde = String(cells[qtdeIdx] ?? '').trim();
    if (!qtde || qtde === '-') return false;
  }

  return true;
}

export function sanitizeOryaTable(headers, rows) {
  if (!headers?.length) return { headers: [], rows: [], droppedIncomplete: 0 };
  const valid = [];
  let dropped = 0;
  for (const row of rows || []) {
    const padded = padRow(row, headers.length);
    if (isCompleteTableRow(headers, padded)) valid.push(padded);
    else dropped += 1;
  }
  return { headers, rows: valid, droppedIncomplete: dropped };
}

export function extractClaimedEquipmentCount(text) {
  const t = String(text || '');
  const patterns = [
    /\b(\d{1,3})\s+equipamentos\b/i,
    /\btop\s+(\d{1,3})\b/i,
    /\bos\s+(\d{1,3})\s+principais\b/i,
    /\b(\d{1,3})\s+registros?\s+completos\b/i,
  ];
  for (const re of patterns) {
    const m = t.match(re);
    if (m) return parseInt(m[1], 10);
  }
  return null;
}

export function isMarkdownTableTruncated(rawContent, headers, rows) {
  const lines = String(rawContent || '').split(/\r?\n/);
  const tableLines = lines.filter((l) => l.trim().startsWith('|'));
  if (!tableLines.length) return false;

  const last = tableLines[tableLines.length - 1];
  const trimmed = last.trim();
  if (!trimmed.endsWith('|')) return true;

  const cells = splitCells(last);
  const colCount = headers?.length || 0;
  if (cells && colCount > 0 && cells.length < colCount) return true;

  const claimed = extractClaimedEquipmentCount(
    lines.slice(0, lines.findIndex((l) => l.trim().startsWith('|'))).join('\n')
  );
  if (claimed != null && rows.length < claimed) return true;

  return false;
}

export function syncIntroWithTableCount(intro, rowCount) {
  let t = String(intro || '').trim();
  if (!t && rowCount > 0) return '📊 Segue o que encontrei na análise:';

  const claimed = extractClaimedEquipmentCount(t);
  if (claimed != null && claimed !== rowCount) {
    t = t.replace(/\b\d{1,3}\s+equipamentos\b/gi, `${rowCount} equipamentos`);
    t = t.replace(/\btop\s+\d{1,3}\b/gi, `top ${rowCount}`);
    t = t.replace(/\bos\s+\d{1,3}\s+principais\b/gi, `os ${rowCount} principais`);
    t = t.replace(/\bseguem\s+os\s+\d{1,3}\b/gi, `seguem os ${rowCount}`);
  }

  t = t.replace(
    /\n*\s*⚠️\s*Exibidos\s+\d+\s+equipamentos[\s\S]*?(interrompida|incomplet)[\s\S]*?\.?\s*/gi,
    '\n'
  );

  return t.trim();
}

/** Conta blocos `**Equipamento: …**` no markdown hierárquico (fonte da verdade visual). */
export function countEquipamentoBlocksInMarkdown(markdown = '') {
  const text = String(markdown || '');
  const re = /\*\*Equipamento(?::|\s*\()/gi;
  let n = 0;
  let m = re.exec(text);
  while (m) {
    n += 1;
    m = re.exec(text);
  }
  return n;
}

/** Alinha o trecho introdutório ao número de blocos de equipamento exibidos. */
export function syncIntroWithEquipBlockCount(intro, blockCount) {
  const n = Math.max(0, Number(blockCount) || 0);
  let t = syncIntroWithTableCount(intro, n);
  t = t
    .replace(/\s*\d+\s+equipamentos?\s+atendem\s+ao\s+critério\s+solicitado\.?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (!t) {
    return n === 1
      ? 'Segue 1 equipamento que atende ao critério solicitado:'
      : n > 0
        ? `Seguem os ${n} equipamentos que atendem ao critério solicitado:`
        : '';
  }

  if (extractClaimedEquipmentCount(t) !== n) {
    t = syncIntroWithTableCount(t, n);
  }
  return t;
}

/** Reconta blocos no painel completo e corrige o intro (texto × blocos exibidos). */
export function syncPanelMarkdownWithEquipBlocks(panelMarkdown = '') {
  const md = String(panelMarkdown || '').trim();
  if (!md) return md;

  const blockCount = countEquipamentoBlocksInMarkdown(md);
  if (blockCount <= 0) return md;

  const equipIdx = md.search(/\*\*Equipamento/i);
  if (equipIdx < 0) return syncIntroWithEquipBlockCount(md, blockCount);

  const introPart = md.slice(0, equipIdx).trim();
  const bodyPart = md.slice(equipIdx).trim();
  const syncedIntro = syncIntroWithEquipBlockCount(introPart, blockCount);
  return [syncedIntro, bodyPart].filter(Boolean).join('\n\n');
}

/**
 * Aplica sanitização final após parse + resumo equip.
 */
export function assertOryaTableResult({
  headers = [],
  rows = [],
  intro = '',
  rawContent = '',
  finishReason = null,
}) {
  const { headers: h, rows: r, droppedIncomplete } = sanitizeOryaTable(headers, rows);
  const claimedBefore = extractClaimedEquipmentCount(intro);
  const likelyTokenCut =
    finishReason === 'MAX_TOKENS' &&
    (isMarkdownTableTruncated(rawContent, h, r) ||
      (claimedBefore != null && claimedBefore > r.length));

  const introSynced = syncIntroWithTableCount(intro, r.length);

  return {
    headers: h,
    rows: r,
    intro: introSynced,
    truncated: likelyTokenCut,
    droppedIncomplete,
    rowCount: r.length,
  };
}
